import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import api from '@/lib/api';
import { formatDateTime, formatDateTimeShort } from '@/utils/date';

interface Application {
  id: string;
  appKey: string;
  name: string;
  description: string | null;
  status: string;
  wechatMiniAppId?: string | null;
  hasWechatMiniAppSecret?: boolean; // 标记是否已配置 AppSecret（不返回实际值）
  lastUsedAt: string | null;
  createdAt: string;
}

interface ApplicationsResponse {
  data: Application[];
  total: number;
}

interface ModuleVisibilityItem {
  id: string;
  appId: string;
  clientType: 'weapp';
  pageKey: string;
  moduleKey: string;
  isVisible: boolean;
  description?: string | null;
  createdAt: string;
  updatedAt: string;
}

const STATUS_COLORS: Record<string, string> = {
  active: 'bg-green-100 text-green-800',
  suspended: 'bg-yellow-100 text-yellow-800',
  deleted: 'bg-red-100 text-red-800',
};

const STATUS_LABELS: Record<string, string> = {
  active: '活跃',
  suspended: '已暂停',
  deleted: '已删除',
};

export default function ApplicationsList() {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [showModuleVisibilityModal, setShowModuleVisibilityModal] = useState(false);
  const [editingApp, setEditingApp] = useState<Application | null>(null);
  const [selectedApp, setSelectedApp] = useState<Application | null>(null);
  const [moduleVisibilitySearch, setModuleVisibilitySearch] = useState('');
  const [newApp, setNewApp] = useState({
    name: '',
    appKey: '',
    description: '',
    wechatMiniAppId: '',
    wechatMiniAppSecret: '',
    // 免费计划配置
    freePlanConfig: {
      name: 'Free Plan',
      description: '基础免费计划',
      dailyCalls: 100,
      features: ['每天 100 次 API 调用', '基础功能'],
    },
  });
  const [editApp, setEditApp] = useState({
    name: '',
    description: '',
    wechatMiniAppId: '',
    wechatMiniAppSecret: '',
  });
  const [newUser, setNewUser] = useState({
    username: '',
    email: '',
    password: '',
    name: '',
    phone: '',
  });
  const [moduleVisibilityForm, setModuleVisibilityForm] = useState({
    clientType: 'weapp' as const,
    pageKey: '',
    moduleKey: '',
    isVisible: true,
    description: '',
  });
  const queryClient = useQueryClient();

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['applications'],
    queryFn: () =>
      api.get<{ status: string; data: ApplicationsResponse | Application[]; message?: string }>(
        '/admin/applications',
      ),
    select: (response): { data: Application[]; total: number } => {
      /**
           * 后端返回的数据结构（经过 TransformInterceptor 包装）：
           * {
           *   status: "success",
           *   code: 200,
           *   data: {                    // ← ApplicationService.findAll() 的返回值
           *     data: Application[],     // ← 应用数组
           *     total: number            // ← 总数
           *   }
           * }
           *
           * 所以我们需要访问 response.data.data.data 和 response.data.data.total
           */
          try {
            const serviceResponse = (response as any)?.data?.data;

            if (!serviceResponse) {
              return { data: [], total: 0 };
            }

            // serviceResponse.data 是应用数组
            if (Array.isArray(serviceResponse.data)) {
              return {
                data: serviceResponse.data as Application[],
                total: Number(serviceResponse.total) || serviceResponse.data.length,
              };
            }

            // 兼容旧版本：serviceResponse 本身是数组
            if (Array.isArray(serviceResponse)) {
              return {
                data: serviceResponse as Application[],
                total: serviceResponse.length,
              };
            }

            return { data: [], total: 0 };
          } catch (error) {
            console.error('Failed to transform applications list response:', error);
            return { data: [], total: 0 };
          }
        },
        staleTime: 0,
        refetchOnMount: true,
      });

  const createMutation = useMutation({
    mutationFn: async (data: typeof newApp) => {
      const response = await api.post('/admin/applications', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['applications'] });
      setShowCreateModal(false);
      setNewApp({
        name: '',
        appKey: '',
        description: '',
        wechatMiniAppId: '',
        wechatMiniAppSecret: '',
        freePlanConfig: {
          name: 'Free Plan',
          description: '基础免费计划',
          dailyCalls: 100,
          features: ['每天 100 次 API 调用', '基础功能'],
        },
      });
    },
    onError: (error: any) => {
      alert(`创建失败: ${error.response?.data?.message || error.message}`);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: typeof editApp }) => {
      const response = await api.put(`/admin/applications/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['applications'] });
      setShowEditModal(false);
      setEditingApp(null);
      setEditApp({
        name: '',
        description: '',
        wechatMiniAppId: '',
        wechatMiniAppSecret: '',
      });
    },
    onError: (error: any) => {
      alert(`更新失败: ${error.response?.data?.message || error.message}`);
    },
  });

  const suspendMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await api.post(`/admin/applications/${id}/suspend`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['applications'] });
    },
  });

  const activateMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await api.post(`/admin/applications/${id}/activate`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['applications'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/admin/applications/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['applications'] });
    },
    onError: (error: any) => {
      alert(`删除失败: ${error.response?.data?.message || error.message}`);
    },
  });

  const createUserMutation = useMutation({
    mutationFn: async ({ appKey, userData }: { appKey: string; userData: typeof newUser }) => {
      const response = await api.post('/admin/users', {
        ...userData,
        appKey,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setShowAddUserModal(false);
      setSelectedApp(null);
      setNewUser({
        username: '',
        email: '',
        password: '',
        name: '',
        phone: '',
      });
      alert('用户创建成功！');
    },
    onError: (error: any) => {
      alert(`创建用户失败: ${error.response?.data?.message || error.message}`);
    },
  });

  const { data: moduleVisibilityData, isLoading: isModuleVisibilityLoading } = useQuery({
    queryKey: ['module-visibility', selectedApp?.id],
    enabled: showModuleVisibilityModal && Boolean(selectedApp?.id),
    queryFn: async () => {
      const response = await api.get<{ status: string; data: ModuleVisibilityItem[] }>(
        `/admin/applications/${selectedApp!.id}/module-visibility`,
      );
      return (response.data as any)?.data ?? [];
    },
  });

  const upsertModuleVisibilityMutation = useMutation({
    mutationFn: async () => {
      if (!selectedApp) {
        throw new Error('未选择应用');
      }

      const response = await api.post(
        `/admin/applications/${selectedApp.id}/module-visibility`,
        moduleVisibilityForm,
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['module-visibility', selectedApp?.id] });
      setModuleVisibilityForm({
        clientType: 'weapp',
        pageKey: '',
        moduleKey: '',
        isVisible: true,
        description: '',
      });
    },
    onError: (error: any) => {
      alert(`保存失败: ${error.response?.data?.message || error.message}`);
    },
  });

  const deleteModuleVisibilityMutation = useMutation({
    mutationFn: async (configId: string) => {
      if (!selectedApp) {
        throw new Error('未选择应用');
      }
      await api.delete(`/admin/applications/${selectedApp.id}/module-visibility/${configId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['module-visibility', selectedApp?.id] });
    },
    onError: (error: any) => {
      alert(`删除失败: ${error.response?.data?.message || error.message}`);
    },
  });

  const applications = data?.data || [];
  const moduleVisibilityItems = (moduleVisibilityData || []).filter((item) => {
    const keyword = moduleVisibilitySearch.trim().toLowerCase();
    if (!keyword) {
      return true;
    }

    return (
      item.pageKey.toLowerCase().includes(keyword) ||
      item.moduleKey.toLowerCase().includes(keyword) ||
      (item.description || '').toLowerCase().includes(keyword)
    );
  });

  const getStatusBadge = (status: string) => {
    const colors = STATUS_COLORS[status] || 'bg-gray-100 text-gray-800';
    const label = STATUS_LABELS[status] || status;
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded ${colors}`}>
        {label}
      </span>
    );
  };

  const handleCreate = () => {
    if (!newApp.name || !newApp.appKey) {
      alert('请填写应用名称和 AppKey');
      return;
    }

    // 检查免费计划配置
    if (!newApp.freePlanConfig.name || !newApp.freePlanConfig.description || !newApp.freePlanConfig.dailyCalls) {
      alert('请填写免费计划的名称、描述和每日调用次数');
      return;
    }

    if (newApp.freePlanConfig.dailyCalls < 1) {
      alert('每日调用次数至少为 1');
      return;
    }

    createMutation.mutate(newApp);
  };

  const handleEdit = (app: Application) => {
    setEditingApp(app);
    setEditApp({
      name: app.name,
      description: app.description || '',
      wechatMiniAppId: app.wechatMiniAppId || '',
      wechatMiniAppSecret: app.wechatMiniAppSecret || '',
    });
    setShowEditModal(true);
  };

  const handleUpdate = () => {
    if (!editingApp || !editApp.name) {
      alert('请填写应用名称');
      return;
    }

    // 构建更新数据，过滤掉空字符串的字段
    const updateData = {
      name: editApp.name,
      description: editApp.description,
      // 只有在有值时才发送 wechatMiniAppId
      ...(editApp.wechatMiniAppId && editApp.wechatMiniAppId.trim() !== ''
        ? { wechatMiniAppId: editApp.wechatMiniAppId }
        : {}),
      // 只有在有值时才发送 wechatMiniAppSecret
      ...(editApp.wechatMiniAppSecret && editApp.wechatMiniAppSecret.trim() !== ''
        ? { wechatMiniAppSecret: editApp.wechatMiniAppSecret }
        : {}),
    };

    updateMutation.mutate({ id: editingApp.id, data: updateData });
  };

  const handleAddUser = (app: Application) => {
    setSelectedApp(app);
    setShowAddUserModal(true);
  };

  const handleCreateUser = () => {
    if (!selectedApp) {
      alert('未选择应用');
      return;
    }

    if (!newUser.username || !newUser.email || !newUser.password) {
      alert('请填写用户名、邮箱和密码');
      return;
    }

    if (newUser.password.length < 6) {
      alert('密码长度至少 6 位');
      return;
    }

    // Email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newUser.email)) {
      alert('邮箱格式不正确');
      return;
    }

    createUserMutation.mutate({
      appKey: selectedApp.appKey,
      userData: newUser,
    });
  };

  const handleDelete = (app: Application) => {
    if (!window.confirm(`确定要删除应用 "${app.name}" 吗？\n\n注意：这是一个软删除操作，数据仍会保留在数据库中。`)) {
      return;
    }
    deleteMutation.mutate(app.id);
  };

  const handleOpenModuleVisibility = (app: Application) => {
    setSelectedApp(app);
    setModuleVisibilitySearch('');
    setModuleVisibilityForm({
      clientType: 'weapp',
      pageKey: '',
      moduleKey: '',
      isVisible: true,
      description: '',
    });
    setShowModuleVisibilityModal(true);
  };

  const handleSaveModuleVisibility = () => {
    const keyPattern = /^[a-z0-9_]+$/;

    if (!moduleVisibilityForm.pageKey || !moduleVisibilityForm.moduleKey) {
      alert('请填写页面 key 和模块 key');
      return;
    }

    if (!keyPattern.test(moduleVisibilityForm.pageKey) || !keyPattern.test(moduleVisibilityForm.moduleKey)) {
      alert('pageKey 和 moduleKey 只能包含小写字母、数字和下划线');
      return;
    }

    upsertModuleVisibilityMutation.mutate();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">应用管理</h1>
          <p className="mt-1 text-sm text-gray-600">
            管理所有应用配置和状态
          </p>
        </div>
        <div className="flex items-center space-x-4">
          <button
            onClick={() => refetch()}
            className="px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50"
          >
            刷新
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-3 py-1 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700"
          >
            创建应用
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  应用名称
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  AppKey
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  描述
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  状态
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  最后使用
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  创建时间
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  操作
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td colSpan={7} className="px-6 py-4 text-center text-sm text-gray-500">
                    加载中...
                  </td>
                </tr>
              ) : applications.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-4 text-center text-sm text-gray-500">
                    暂无应用
                  </td>
                </tr>
              ) : (
                applications.map((app) => (
                  <tr key={app.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {app.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 font-mono">
                      {app.appKey}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate">
                      {app.description || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {getStatusBadge(app.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {app.lastUsedAt
                        ? formatDateTimeShort(app.lastUsedAt)
                        : '从未使用'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {formatDateTime(app.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="flex items-center space-x-3">
                        <button
                          onClick={() => handleEdit(app)}
                          className="text-blue-600 hover:text-blue-800 font-medium"
                        >
                          编辑
                        </button>
                        <button
                          onClick={() => handleOpenModuleVisibility(app)}
                          className="text-purple-600 hover:text-purple-800 font-medium"
                        >
                          模块开关
                        </button>
                        {app.status === 'active' && (
                          <button
                            onClick={() => handleAddUser(app)}
                            className="text-indigo-600 hover:text-indigo-800 font-medium"
                          >
                            添加用户
                          </button>
                        )}
                        {app.status === 'active' ? (
                          <button
                            onClick={() => suspendMutation.mutate(app.id)}
                            className="text-yellow-600 hover:text-yellow-800 font-medium"
                          >
                            暂停
                          </button>
                        ) : app.status === 'suspended' ? (
                          <button
                            onClick={() => activateMutation.mutate(app.id)}
                            className="text-green-600 hover:text-green-800 font-medium"
                          >
                            激活
                          </button>
                        ) : null}
                        {app.status !== 'deleted' && (
                          <button
                            onClick={() => handleDelete(app)}
                            className="text-red-600 hover:text-red-800 font-medium"
                          >
                            删除
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* 创建应用模态框 */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">创建新应用</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  应用名称 *
                </label>
                <input
                  type="text"
                  value={newApp.name}
                  onChange={(e) => setNewApp({ ...newApp, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="我的应用"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  AppKey *
                </label>
                <input
                  type="text"
                  value={newApp.appKey}
                  onChange={(e) => setNewApp({ ...newApp, appKey: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="ak_myapp001"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  描述
                </label>
                <textarea
                  value={newApp.description}
                  onChange={(e) => setNewApp({ ...newApp, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                  placeholder="应用描述（可选）"
                />
              </div>
              <div className="border-t pt-4 mt-4">
                <h4 className="text-sm font-medium text-gray-900 mb-3">免费计划配置 *</h4>
                <p className="text-xs text-gray-500 mb-3">
                  创建应用时会自动创建一个免费计划，供新用户注册时使用
                </p>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      计划名称 *
                    </label>
                    <input
                      type="text"
                      value={newApp.freePlanConfig.name}
                      onChange={(e) =>
                        setNewApp({
                          ...newApp,
                          freePlanConfig: { ...newApp.freePlanConfig, name: e.target.value },
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Free Plan"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      计划描述 *
                    </label>
                    <textarea
                      value={newApp.freePlanConfig.description}
                      onChange={(e) =>
                        setNewApp({
                          ...newApp,
                          freePlanConfig: { ...newApp.freePlanConfig, description: e.target.value },
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      rows={2}
                      placeholder="基础免费计划"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      每日调用次数 *
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={newApp.freePlanConfig.dailyCalls}
                      onChange={(e) =>
                        setNewApp({
                          ...newApp,
                          freePlanConfig: {
                            ...newApp.freePlanConfig,
                            dailyCalls: parseInt(e.target.value) || 0,
                          },
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="100"
                    />
                    <p className="text-xs text-gray-500 mt-1">免费用户每天可以调用的 API 次数上限</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      功能特性
                    </label>
                    <textarea
                      value={newApp.freePlanConfig.features?.join('\n') || ''}
                      onChange={(e) =>
                        setNewApp({
                          ...newApp,
                          freePlanConfig: {
                            ...newApp.freePlanConfig,
                            features: e.target.value.split('\n').filter((line) => line.trim()),
                          },
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      rows={3}
                      placeholder="每天 100 次 API 调用&#10;基础功能"
                    />
                    <p className="text-xs text-gray-500 mt-1">每行一个功能特性（可选）</p>
                  </div>
                </div>
              </div>
              <div className="border-t pt-4 mt-4">
                <h4 className="text-sm font-medium text-gray-900 mb-3">微信小程序配置（可选）</h4>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      小程序 AppID
                    </label>
                    <input
                      type="text"
                      value={newApp.wechatMiniAppId}
                      onChange={(e) => setNewApp({ ...newApp, wechatMiniAppId: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="wx1234567890abcdef"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      小程序 AppSecret
                    </label>
                    <input
                      type="password"
                      value={newApp.wechatMiniAppSecret}
                      onChange={(e) => setNewApp({ ...newApp, wechatMiniAppSecret: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="******"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                取消
              </button>
              <button
                onClick={handleCreate}
                disabled={createMutation.isPending}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {createMutation.isPending ? '创建中...' : '创建'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 编辑应用模态框 */}
      {showEditModal && editingApp && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">编辑应用</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  应用名称 *
                </label>
                <input
                  type="text"
                  value={editApp.name}
                  onChange={(e) => setEditApp({ ...editApp, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="我的应用"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  AppKey
                </label>
                <input
                  type="text"
                  value={editingApp.appKey}
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500 cursor-not-allowed"
                />
                <p className="text-xs text-gray-500 mt-1">AppKey 不可修改</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  描述
                </label>
                <textarea
                  value={editApp.description}
                  onChange={(e) => setEditApp({ ...editApp, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                  placeholder="应用描述（可选）"
                />
              </div>
              <div className="border-t pt-4 mt-4">
                <h4 className="text-sm font-medium text-gray-900 mb-3">微信小程序配置</h4>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      小程序 AppID
                    </label>
                    <input
                      type="text"
                      value={editApp.wechatMiniAppId}
                      onChange={(e) => setEditApp({ ...editApp, wechatMiniAppId: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="wx1234567890abcdef"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      小程序 AppSecret
                    </label>
                    <input
                      type="text"
                      value={editApp.wechatMiniAppSecret}
                      onChange={(e) => setEditApp({ ...editApp, wechatMiniAppSecret: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder={editingApp?.hasWechatMiniAppSecret ? "已配置（留空保持不变）" : "未配置"}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {editingApp?.hasWechatMiniAppSecret ? (
                        <span className="text-green-600">✓ 已配置 · 留空表示不修改现有配置</span>
                      ) : (
                        <span>留空表示不设置</span>
                      )}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setEditingApp(null);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                取消
              </button>
              <button
                onClick={handleUpdate}
                disabled={updateMutation.isPending}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {updateMutation.isPending ? '更新中...' : '更新'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 添加用户模态框 */}
      {showAddUserModal && selectedApp && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-2">添加用户到应用</h3>
            <p className="text-sm text-gray-600 mb-4">
              应用: <span className="font-medium">{selectedApp.name}</span> ({selectedApp.appKey})
            </p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  用户名 *
                </label>
                <input
                  type="text"
                  value={newUser.username}
                  onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="username"
                />
                <p className="text-xs text-gray-500 mt-1">只能包含字母、数字和下划线</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  邮箱 *
                </label>
                <input
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="user@example.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  密码 *
                </label>
                <input
                  type="password"
                  value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="******"
                />
                <p className="text-xs text-gray-500 mt-1">至少 6 位</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  姓名
                </label>
                <input
                  type="text"
                  value={newUser.name}
                  onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="张三"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  手机号
                </label>
                <input
                  type="tel"
                  value={newUser.phone}
                  onChange={(e) => setNewUser({ ...newUser, phone: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="13800138000"
                />
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                <p className="text-sm text-blue-800">
                  <strong>说明：</strong>
                </p>
                <ul className="text-xs text-blue-700 mt-1 space-y-1">
                  <li>• 用户将自动绑定到该应用的免费计划</li>
                  <li>• 用户可以使用该账号登录应用</li>
                  <li>• 用户将获得默认的每日调用配额</li>
                </ul>
              </div>
            </div>
            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowAddUserModal(false);
                  setSelectedApp(null);
                  setNewUser({
                    username: '',
                    email: '',
                    password: '',
                    name: '',
                    phone: '',
                  });
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                取消
              </button>
              <button
                onClick={handleCreateUser}
                disabled={createUserMutation.isPending}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50"
              >
                {createUserMutation.isPending ? '创建中...' : '创建用户'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showModuleVisibilityModal && selectedApp && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-5xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <h3 className="text-lg font-semibold">模块开关</h3>
                <p className="text-sm text-gray-600 mt-1">
                  应用: <span className="font-medium">{selectedApp.name}</span> ({selectedApp.appKey})
                </p>
              </div>
              <button
                onClick={() => {
                  setShowModuleVisibilityModal(false);
                  setSelectedApp(null);
                }}
                className="px-3 py-1 text-sm font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200"
              >
                关闭
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="border border-gray-200 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-gray-900 mb-3">新增或更新配置</h4>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">客户端</label>
                    <input
                      type="text"
                      value="weapp"
                      disabled
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">页面 key *</label>
                    <input
                      type="text"
                      value={moduleVisibilityForm.pageKey}
                      onChange={(e) =>
                        setModuleVisibilityForm((prev) => ({ ...prev, pageKey: e.target.value.trim() }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="deduction"
                    />
                    <p className="text-xs text-gray-500 mt-1">示例：`deduction`、`profile`</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">模块 key *</label>
                    <input
                      type="text"
                      value={moduleVisibilityForm.moduleKey}
                      onChange={(e) =>
                        setModuleVisibilityForm((prev) => ({ ...prev, moduleKey: e.target.value.trim() }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="almanac"
                    />
                    <p className="text-xs text-gray-500 mt-1">示例：`almanac`、`vip_card`</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">显示状态</label>
                    <select
                      value={moduleVisibilityForm.isVisible ? 'visible' : 'hidden'}
                      onChange={(e) =>
                        setModuleVisibilityForm((prev) => ({
                          ...prev,
                          isVisible: e.target.value === 'visible',
                        }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="visible">显示</option>
                      <option value="hidden">隐藏</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">备注</label>
                    <textarea
                      value={moduleVisibilityForm.description}
                      onChange={(e) =>
                        setModuleVisibilityForm((prev) => ({ ...prev, description: e.target.value }))
                      }
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="例如：首页黄历卡片"
                    />
                  </div>
                  <button
                    onClick={handleSaveModuleVisibility}
                    disabled={upsertModuleVisibilityMutation.isPending}
                    className="w-full px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    {upsertModuleVisibilityMutation.isPending ? '保存中...' : '保存配置'}
                  </button>
                </div>
              </div>

              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between gap-3 mb-3">
                  <h4 className="text-sm font-semibold text-gray-900">已配置模块</h4>
                  <input
                    type="text"
                    value={moduleVisibilitySearch}
                    onChange={(e) => setModuleVisibilitySearch(e.target.value)}
                    className="w-56 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="搜索 pageKey / moduleKey"
                  />
                </div>

                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">页面</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">模块</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">状态</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">备注</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">操作</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {isModuleVisibilityLoading ? (
                        <tr>
                          <td colSpan={5} className="px-3 py-4 text-center text-sm text-gray-500">
                            加载中...
                          </td>
                        </tr>
                      ) : moduleVisibilityItems.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="px-3 py-4 text-center text-sm text-gray-500">
                            暂无配置
                          </td>
                        </tr>
                      ) : (
                        moduleVisibilityItems.map((item) => (
                          <tr key={item.id}>
                            <td className="px-3 py-3 text-sm text-gray-700 font-mono">{item.pageKey}</td>
                            <td className="px-3 py-3 text-sm text-gray-700 font-mono">{item.moduleKey}</td>
                            <td className="px-3 py-3 text-sm">
                              <span
                                className={`px-2 py-1 text-xs font-medium rounded ${
                                  item.isVisible
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-red-100 text-red-800'
                                }`}
                              >
                                {item.isVisible ? '显示' : '隐藏'}
                              </span>
                            </td>
                            <td className="px-3 py-3 text-sm text-gray-600">{item.description || '-'}</td>
                            <td className="px-3 py-3 text-sm">
                              <div className="flex items-center gap-3">
                                <button
                                  onClick={() =>
                                    setModuleVisibilityForm({
                                      clientType: item.clientType,
                                      pageKey: item.pageKey,
                                      moduleKey: item.moduleKey,
                                      isVisible: item.isVisible,
                                      description: item.description || '',
                                    })
                                  }
                                  className="text-blue-600 hover:text-blue-800 font-medium"
                                >
                                  编辑
                                </button>
                                <button
                                  onClick={() => {
                                    if (!window.confirm(`确定删除配置 ${item.pageKey}/${item.moduleKey} 吗？`)) {
                                      return;
                                    }
                                    deleteModuleVisibilityMutation.mutate(item.id);
                                  }}
                                  className="text-red-600 hover:text-red-800 font-medium"
                                >
                                  删除
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
